import Drawer from '@mui/material/Drawer';
import { useEffect, useState } from 'react';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';
import { useLaunchDarkly } from '../../launchdarkly';
import SidebarHeader from './component/SidebarHeader';
import UserMenu from './component/UserMenu';
import TabMenu from './component/TabMenu';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '../../utils/Constants';
import { fromLayout } from '../../store';
import routes from '../routes';
import { RouteConfig } from '../../routes';

const useStyles = makeStyles(theme => ({
  drawerPadding: {
    overflow: 'auto',
    height: '100%',
    [theme.breakpoints.up('md')]: {
      overflow: 'auto',
      width: SIDEBAR_WIDTH,
      position: 'relative',
      height: '100%',
    },
  },
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: SIDEBAR_COLLAPSED_WIDTH,
  [theme.breakpoints.up('sm')]: {
    width: SIDEBAR_COLLAPSED_WIDTH,
  },
});

const StyledDrawer = styled(Drawer, { shouldForwardProp: prop => prop !== 'open' })(
  ({ theme }) => ({
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    '& .MuiDrawer-paper': {
      backgroundColor: '#f7f9f8',
      borderRight: '1px solid #eef2f0',
      padding: '16px 0',
    },
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export const Sidebar = (
  { filteredRoutes }: {
    filteredRoutes: RouteConfig[];
  },
) => {
  const matches = useMediaQuery('(max-width:1280px)');
  const { pathname } = useLocation();
  const currentRoute = routes.find(route => pathname === route.path);
  const [open, setOpen] = useState(true);
  const classes = useStyles();
  const { flags } = useLaunchDarkly();
  const showSidebar = useSelector(fromLayout.selectShowSidebar);
  const handleSidebarToggle = () => {
    setOpen(!open);
  };

  useEffect(() => {
    if (currentRoute?.showInMenu) {
      setOpen(!matches);
    } else {
      setOpen(false);
    }
  }, [matches, currentRoute]);

  return (
    <StyledDrawer
      variant="permanent"
      open={open}
      sx={theme => ({
        display: !flags?.sidebarEnabled || !showSidebar ? 'none' : 'flex',
        [theme.breakpoints.down('tablet')]: {
          display: 'none',
        },
      })}
      classes={{
        paper: clsx({
          [classes.drawerPadding]: flags?.navigationBarEnabled,
        }),
      }}
    >
      {/* Sidebar Header */}
      <SidebarHeader
        open={open}
        collapsible
        sidebarToggle={handleSidebarToggle}
      />
      {/* User Menu */}
      <UserMenu
        open={open}
        expandSidebar={() => setOpen(true)}
      />
      {/* Tab Menu */}
      <TabMenu
        open={open}
        filteredRoutes={filteredRoutes}
      />
    </StyledDrawer>
  );
};
